
import { UseFormReturn } from "react-hook-form";
import { FormValues } from "@/types/loan";
import { FormItem, FormLabel } from "@/components/ui/form";
import { LoanCalculationSummary } from "@/components/loan/LoanCalculationSummary";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { useEffect } from "react";
import { LoanPurposeField } from "./LoanPurposeField";

interface HebrewLoanDetailsSectionProps {
  form: UseFormReturn<FormValues>;
  loanAmount: number;
  setLoanAmount: (value: number) => void;
  loanTerm: number;
  setLoanTerm: (value: number) => void;
  monthlyPayment: number;
  totalPaid: number;
  interestAndFees: number;
  interest: number;
  monthlyFee: number;
  setupFee: number;
  setLoanType: (value: string) => void;
}

export const HebrewLoanDetailsSection = ({
  form,
  loanAmount,
  setLoanAmount,
  loanTerm,
  setLoanTerm,
  monthlyPayment,
  totalPaid,
  interestAndFees,
  interest,
  monthlyFee,
  setupFee,
  setLoanType
}: HebrewLoanDetailsSectionProps) => {
  // Set initial loan type if needed
  useEffect(() => {
    const currentLoanType = form.getValues().loanType;
    if (currentLoanType && setLoanType) {
      setLoanType(currentLoanType);
    } else {
      // Set default loan type if not present
      setLoanType("kulutusluotto");
    }
  }, [form, setLoanType]);

  return (
    <div className="p-5 bg-blue-50 rounded-lg">
      <h3 className="text-lg font-medium text-blue-700 mb-4">פרטי ההלוואה</h3>
      
      <LoanPurposeField form={form} language="en" />
      
      <HebrewLoanAmountSection 
        loanAmount={loanAmount} 
        setLoanAmount={setLoanAmount} 
      />
      
      <HebrewLoanTermSection 
        loanTerm={loanTerm} 
        setLoanTerm={setLoanTerm} 
      />
      
      <LoanCalculationSummary 
        monthlyPayment={monthlyPayment}
        totalPaid={totalPaid}
        interestAndFees={interestAndFees}
        interestRate={interest}
        monthlyFee={monthlyFee}
        setupFee={setupFee}
        language="he"
      />
    </div>
  );
};

// Hebrew version of loan amount section
export const HebrewLoanAmountSection = ({ loanAmount, setLoanAmount }: { loanAmount: number, setLoanAmount: (value: number) => void }) => {
  return (
    <div className="mb-6">
      <div className="flex justify-between mb-2">
        <Label htmlFor="loan-amount" className="text-gray-700 font-medium">סכום ההלוואה</Label>
        <span className="font-semibold text-blue-700 bg-blue-50 px-3 py-1 rounded-full">{loanAmount.toLocaleString()} €</span>
      </div>
      <div className="relative mt-3 p-3 bg-white rounded-lg shadow-sm">
        <Slider 
          id="loan-amount" 
          min={1000} 
          max={70000} 
          step={1000} 
          value={[loanAmount]} 
          onValueChange={(value) => setLoanAmount(value[0])}
          className="my-4"
          dir="ltr"
        />
        <div className="flex justify-between text-xs text-gray-600 font-medium mt-3">
          <span className="bg-blue-50 px-2 py-1 rounded-full">€ 1,000</span>
          <span className="bg-blue-50 px-2 py-1 rounded-full">€ 35,000</span>
          <span className="bg-blue-50 px-2 py-1 rounded-full">€ 70,000</span>
        </div>
      </div>
    </div>
  );
};

export const HebrewLoanTermSection = ({ loanTerm, setLoanTerm }: { loanTerm: number, setLoanTerm: (value: number) => void }) => {
  return (
    <div className="mb-6">
      <div className="flex justify-between mb-2">
        <Label htmlFor="loan-term" className="text-gray-700 font-medium">תקופת ההלוואה</Label>
        <span className="font-semibold text-blue-700 bg-blue-50 px-3 py-1 rounded-full">{loanTerm} שנים</span>
      </div>
      <div className="relative mt-3 p-3 bg-white rounded-lg shadow-sm">
        <Slider 
          id="loan-term" 
          min={1} 
          max={15} 
          step={1} 
          value={[loanTerm]} 
          onValueChange={(value) => setLoanTerm(value[0])}
          className="my-4"
          dir="ltr"
        />
        <div className="flex justify-between text-xs text-gray-600 font-medium mt-3">
          <span className="bg-blue-50 px-2 py-1 rounded-full">שנה 1</span>
          <span className="bg-blue-50 px-2 py-1 rounded-full">7 שנים</span>
          <span className="bg-blue-50 px-2 py-1 rounded-full">15 שנה</span>
        </div>
      </div>
    </div>
  );
};
